import {
  AbstractApiResponse,
  Request,
  RequestState,
  EngineRequestResultDBData
} from 'vrlcm-gui-types';
import { Http, debugLogger } from 'vrlcm-gui-utils';

import { RequestApi } from '../request';

export class EngineApi {
  public static async triggerJob<JD>(
    jobUrl: string,
    jobData: JD,
    postInitFunction?: (cancelFunction: () => void) => void,
    requestType: 'GET' | 'POST' = 'POST'
  ) {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    const response = await (requestType === 'POST'
      ? Http.post<Request>(jobUrl, jobData)
      : Http.get<Request>(jobUrl));
    return response.data;
  }

  /**
   * Steps to trigger an async job and get the result
   * 1. Trigger a job and get `requestId`.
   * 2. Use the `requestId` to fetch request object which contains `executionId`.
   * 3. `executionId` within request object can be null, so poll the request object until
   *    `executionId` is valid.
   * 4. Use the `executionId` to fetch the status of the execution using engine status API.
   * 5. Keep polling for changes in status from `INPROGRESS` to `COMPLETED` or `FAILED`.
   * 5. Once the status is `COMPLETED` or `FAILED`, fetch job result from engine result API.
   *
   */
  public static async triggerJobAndReturnResponse<JD, RD>(
    jobUrl: string,
    jobData: JD,
    postInitFunction?: (cancelFunction: () => void) => void,
    requestType: 'GET' | 'POST' = 'POST',
    extractEngineRequestResultData?: <T>(
      engineRequestResultDBData: EngineRequestResultDBData
    ) => T | undefined
  ): Promise<AbstractApiResponse<RD>> {
    const cancelFunctions: Array<() => void> = [];
    if (postInitFunction) {
      const cancel = () => cancelFunctions.forEach(cf => cf());
      postInitFunction(cancel);
    }
    try {
      let response;
      try {
        response = await EngineApi.triggerJob(
          jobUrl,
          jobData,
          cf => cancelFunctions.push(cf),
          requestType
        );
      } catch (e) {
        debugLogger(
          'Error on job execution API. url - ',
          jobUrl,
          ' err - ',
          e,
          ' error response - ',
          e.response
        );
        throw Error(e);
      }
      const engineResult = await RequestApi.pollRequestForCompletionAndExtractEngineResult(
        response.vmid,
        extractEngineRequestResultData,
        1200,
        cancelFn => cancelFunctions.push(cancelFn)
      );
      return {
        success: engineResult.state === RequestState.COMPLETED,
        data: engineResult.result,
        when: new Date()
      };
    } catch (e) {
      debugLogger(
        'caught error while triggering job, job url - ',
        jobUrl,
        ' job data - ',
        jobData,
        ' error - ',
        e
      );
      return {
        success: false,
        when: new Date()
      };
    }
  }
}
